import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/** Stapel van open dialogen, zodat Escape alleen de bovenste sluit en de
 *  body-scroll pas terugkomt als de laatste dicht is. */
const OPEN_DIALOGS: HTMLElement[] = [];

/** Modale dialoog met focus-trap, focus-herstel en Esc-sluiten.
 *  De EJS-app had geen van drieën: focus liep achter de backdrop door en
 *  keerde na sluiten niet terug naar de knop die de dialoog opende. */
export function Dialog({ title, children, onClose, width = '34rem' }: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: string;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  // onClose via een ref: de aanroeper geeft vaak een inline arrow mee die elke
  // render nieuw is. Als afhankelijkheid van het effect hieronder liep dat dan
  // bij elke toetsaanslag opnieuw en zette de focus terug op het sluitkruisje —
  // typen in een tekstvak in de dialoog werd zo onmogelijk.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';

    const focusables = () =>
      [...(panel.current?.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
      ) ?? [])].filter((el) => el.offsetParent !== null);

    // Heeft een veld met autoFocus de focus al gepakt, laat hem daar staan;
    // anders het eerste focusbare element (meestal het sluitkruisje).
    if (!panel.current?.contains(document.activeElement)) {
      focusables()[0]?.focus() ?? panel.current?.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      // Alleen de bovenste dialoog reageert op Escape. stopPropagation() helpt
      // hier niet: twee luisteraars op hetzelfde document horen elkaar niet af,
      // dus met twee dialogen open sloten ze allebei tegelijk.
      if (e.key === 'Escape') {
        if (OPEN_DIALOGS[OPEN_DIALOGS.length - 1] !== panel.current) return;
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      // Staat de focus buiten de dialoog (op <body>, of op een element dat
      // net is verdwenen)? Haal hem terug in plaats van hem te laten lopen.
      if (!panel.current?.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    if (panel.current) OPEN_DIALOGS.push(panel.current);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = OPEN_DIALOGS.indexOf(panel.current!);
      if (i !== -1) OPEN_DIALOGS.splice(i, 1);
      // Alleen de body weer laten scrollen als er geen dialoog meer open is.
      if (OPEN_DIALOGS.length === 0) {
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY });
      }
      returnTo.current?.focus();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center overflow-y-auto bg-scrim px-4 py-[8vh] backdrop-blur-[3px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        style={{ width, maxWidth: '100%' }}
        className="rounded-xl border border-hairline bg-raised shadow-xl motion-safe:animate-scale-in"
      >
        <div className="flex items-center justify-between border-b border-hairline px-5 py-3">
          <h2 id={titleId} className="text-xl font-semibold text-ink">{title}</h2>
          <button
            type="button" onClick={onClose} aria-label="Sluiten"
            className="rounded-md p-1.5 text-ink-3 transition hover:bg-surface-hover hover:text-ink"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
